import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import latinize from 'latinize';
import TimeLimitContext from '../../context/TimeLimitContext';
import StealCapContext from '../../context/StealCapContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { selectRandomCombination } from '../../helpers/selectRandomCombination';
import { getSquareCombinations } from '../../services/api';
import { winningPositions } from '../../helpers/winningPositions';
import { checkPlayer } from '../../helpers/checkPlayer';
import { saveGameToken, savePoints } from '../../helpers/botPoints';
import { changeDifficulty } from '../../helpers/streakManagement';
import { pickBotSquareOrder, shouldBotShield } from '../../helpers/botMove';
import clubMapping from '../../data/clupMapping.json';
import obvioBotImg from '../../img/home/obvio-bot.png';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../../components/Navbar/Navbar';
import AdBanner from '../../components/AdBanner/AdBanner';
import Board from '../../components/Board/Board';
import ScoreBoard from '../../components/ScoreBoard/ScoreBoard';
import NextButton from '../../components/NextButton/NextButton';
import PlayerSelected from '../../components/PlayerSelected/PlayerSelected';
import CountdownTimer from '../../components/CountdownTimer/CountdownTimer';
import BotCharacter from '../../components/BotCharacter/BotCharacter';
import { pickFour12Character } from '../../components/BotCharacter/four12Characters';
import useClassicRival from '../../components/BotCharacter/useClassicRival';
import RivalPicker from '../../components/BotCharacter/RivalPicker';
import './SoloBotPage.css';

const backendURL = (process.env.REACT_APP_BACKEND_URL || 'https://api.footy-games.com.ar');

const SoloBotPage = () => {
  const navigate = useNavigate();
  const [turn, setTurn] = useState('X');
  const [squares, setSquares] = useState(Array(9).fill({ value: '', playerName: '', claims: 0, claimedBy: [], shielded: false }));
  const [winningSquares, setWinningSquares] = useState([]);
  const [score, setScore] = useState({
    X: 0,
    O: 0,
  });
  const [difficulty, setDifficulty] = useState('medium');
  const [league, setLeague] = useState('normal');
  const [items, setItems] = useState(Array(6).fill({}));
  const [combos, setCombos] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const { timeLimit } = useContext(TimeLimitContext);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [gameToken, setGameToken] = useState(null);
  const [blockedTeams, setBlockedTeams] = useState([]);
  const [wrongPickCount, setWrongPickCount] = useState(0);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const hasAnyData = squares.some(square => square.value !== '' || square.playerName !== '');
  const location = useLocation();
  const variant = location.state?.variant || 'classic';
  const isStealMode = variant === 'steal';
  const isFour12Mode = variant === 'four12';
  const isClassicMode = variant === 'classic';
  // Clásico: el rival es el bot o uno de los personajes de 412, aleatorio por partida o fijo.
  const classicRival = useClassicRival(isClassicMode);
  const { nextRound: nextClassicRival } = classicRival;
  const { stealCap } = useContext(StealCapContext);
  const [consecutiveSkips, setConsecutiveSkips] = useState(0);
  const [stealFlashIdx, setStealFlashIdx] = useState(null);
  // Modo 412: casilla "elegida" este turno (no se puede tocar ninguna otra hasta resolverla o
  // pasar el turno) y el set de índices de `items` (0-5, columnas 0-2 / filas 3-5) ya revelados
  // — una vez revelado un requisito queda visible para siempre, aunque se pase el turno.
  const [lockedSquare, setLockedSquare] = useState(null);
  const [revealedItems, setRevealedItems] = useState(() => new Set());
  // Modo 412: en vez del bot genérico juega uno de los 5 personajes, sorteado por ronda
  // (cada reset elige uno DISTINTO al anterior). Fuera de 412 queda null y se ve el bot.
  const [four12Character, setFour12Character] = useState(
    () => (variant === 'four12' ? pickFour12Character() : null)
  );
  // Blindaje: una vez por partida, cada jugador (vos o el bot) puede elegir una casilla que,
  // al jugarla correctamente, queda intocable para siempre (sin importar el tope de jugadas).
  const [shieldUsed, setShieldUsed] = useState({ X: false, O: false });
  const [armedShieldPlayer, setArmedShieldPlayer] = useState(null); // 'X' | null (el bot decide solo)

  const itemMaping = useMemo(() => ({
    0: [items[0], items[3]],
    1: [items[1], items[3]],
    2: [items[2], items[3]],
    3: [items[0], items[4]],
    4: [items[1], items[4]],
    5: [items[2], items[4]],
    6: [items[0], items[5]],
    7: [items[1], items[5]],
    8: [items[2], items[5]],
  }), [items]);

  // Modo 412: índices (0-5) dentro de `items` que corresponde revelar al jugar una casilla —
  // NO los valores (esos ya los da itemMaping), sino la posición para poder marcarlos en
  // `revealedItems` y que Board.js sepa qué TeamSquare mostrar y cuál mantener oculto.
  const squareToItemIndices = (square) => [square % 3, 3 + Math.floor(square / 3)];

  useEffect(() => {
    if (location.state && location.state.mode) {
      if (location.state.mode === 'normal') {
        setLeague('normal');
        getSquareCombinations()
          .then((data) => {
            setCombos(data);
            const initialCombination = selectRandomCombination(data, blockedTeams);
            setItems(initialCombination);
          })
          .catch((err) => console.error("Error al obtener combinaciones:", err));
      } else {
        return;
      }
    }
  }, [location.state, blockedTeams]);


  useEffect(() => {
    setTimeRemaining(timeLimit);

    const timer = setInterval(() => {
      setTimeRemaining((prevTimeRemaining) => {
        if (prevTimeRemaining === 0) {
          handleSkipTurn();
          setSelectedPlayer(null);
          return timeLimit;
        }
        return prevTimeRemaining - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLimit, turn]);

  useEffect(() => {
    saveGameToken(squares, setGameToken);
  }, [squares]);

  const handleSkipTurn = () => {
    // Modo 412: pasar el turno libera el compromiso con la casilla elegida (lo ya revelado
    // sigue visible, pero el próximo turno puede elegir cualquier otra vacía).
    setLockedSquare(null);
    setTurn((prevTurn) => (prevTurn === 'X' ? 'O' : 'X'));
  }

  const handleChangeItems = useCallback(() => {
    if (league === 'normal' && combos.length > 0) {
      const newCombination = selectRandomCombination(combos, blockedTeams);
      setItems(newCombination);
    }
  }, [league, combos, blockedTeams]);


  const handlePlayerSelect = (player) => {
    // Modo 412: ya hay una casilla "elegida" esperando resolverse — elegir un jugador del
    // buscador intenta colocarlo ahí directamente (no hace falta un click aparte).
    if (isFour12Mode && lockedSquare !== null) {
      attemptPlacement(lockedSquare, player);
      return;
    }
    setSelectedPlayer(player);
  }

  // Extraído de handleClick para poder dispararlo tanto desde un click (Clásico/Robo) como
  // desde elegir un jugador en el buscador (Modo 412, donde la casilla ya está comprometida).
  const attemptPlacement = (square, playerToPlace) => {
    const sq = squares[square];
    const [item1, item2] = itemMaping[square];
    const playedInItem1 = checkPlayer(playerToPlace, item1);
    const playedInItem2 = checkPlayer(playerToPlace, item2);

    if (playedInItem1 && playedInItem2) {
      const wasSteal = isStealMode && sq.value !== '' && sq.value !== turn;
      const wasReinforce = isStealMode && sq.value !== '' && sq.value === turn;
      const stolenFromName = sq.playerName;
      const newClaims = (sq.claims || 0) + 1;
      // Reforzar tu propia casilla (poner otro jugador sobre una que ya era tuya) la blinda para
      // siempre al instante, GRATIS: no gasta el blindaje único del botón.
      const willShield = wasReinforce || (isStealMode && armedShieldPlayer === turn);
      const consumesShieldCharge = willShield && !wasReinforce;
      let newSquares = [...squares];
      newSquares.splice(square, 1, {
        value: turn,
        playerName: playerToPlace.nombre,
        playerImg: playerToPlace.img || null,
        claims: newClaims,
        claimedBy: isStealMode ? [...(sq.claimedBy || []), playerToPlace._id] : (sq.claimedBy || []),
        shielded: willShield ? true : (sq.shielded || false),
      });
      setConsecutiveSkips(0);
      checkForWinner(newSquares);
      setSquares(newSquares);
      setSelectedPlayer(null);
      if (isFour12Mode) setLockedSquare(null); // acertó: se libera el compromiso con la casilla
      if (consumesShieldCharge) {
        setShieldUsed(prev => ({ ...prev, [turn]: true }));
        setArmedShieldPlayer(null);
        if (wasSteal) {
          setStealFlashIdx(square);
          setTimeout(() => setStealFlashIdx(null), 1200);
        }
        toast.success(
          wasSteal
            ? `🛡️🔥 ¡Le robaste la casilla a ${stolenFromName} y la blindaste para siempre!`
            : '🛡️ ¡Casilla blindada para siempre! Nadie va a poder tocarla.',
          {
            position: "top-right", autoClose: 4000, hideProgressBar: false,
            closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
          }
        );
      } else if (wasReinforce) {
        toast.success('🛡️ ¡Reforzaste tu casilla y quedó blindada para siempre! (No gastó tu blindaje).', {
          position: "top-right", autoClose: 4000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
        });
      } else if (wasSteal) {
        setStealFlashIdx(square);
        setTimeout(() => setStealFlashIdx(null), 1200);
        toast.success(`🔥 ¡Le robaste la casilla a ${stolenFromName}!`, {
          position: "top-right", autoClose: 3000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
        });
      }
    } else if (isFour12Mode) {
      // Igual que Clásico/Robo: errar pierde el turno. handleSkipTurn() ya limpia
      // lockedSquare, así que el rival (o vos, en tu próximo turno) puede elegir cualquier
      // casilla vacía de nuevo — no queda "trabado" reintentando la misma para siempre.
      toast.warn('Ese jugador no cumple los 2 requisitos de esta casilla. 😔 Perdiste el turno.', {
        position: "top-right", autoClose: 4000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
      });
      setWrongPickCount(prev => prev + 1);
      setConsecutiveSkips(prev => prev + 1);
      handleSkipTurn();
      setSelectedPlayer(null);
    } else {
      toast.warn('The player does not meet the requirements. 😔You lost the turn.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      setWrongPickCount(prev => prev + 1);
      setConsecutiveSkips(prev => prev + 1);
      handleSkipTurn();
      setSelectedPlayer(null);
    }
  };

  const handleClick = (square) => {
    const sq = squares[square];

    if (isFour12Mode) {
      if (sq.value !== '') return; // sin robo en 412: ocupada, Square ya bloquea el click
      // El humano siempre es 'X' en esta página (vs bot) — sin esto, un click durante el
      // turno del bot (sin buscador ni selectedPlayer que lo frenen, a diferencia de
      // Clásico/Robo) podría "elegir" una casilla fuera de turno.
      if (turn !== 'X') return;
      if (lockedSquare !== null && lockedSquare !== square) {
        toast.info('Primero resolvé la casilla que elegiste (o usá Skip Turn).', {
          position: "top-right", autoClose: 3000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
        });
        return;
      }
      if (lockedSquare === null) {
        setLockedSquare(square);
        const [colIdx, rowIdx] = squareToItemIndices(square);
        setRevealedItems(prev => new Set(prev).add(colIdx).add(rowIdx));
        return;
      }
      // lockedSquare === square: ya está revelada: reintentar con el jugador ya elegido, si hay.
      if (selectedPlayer) attemptPlacement(square, selectedPlayer);
      return;
    }

    if (!selectedPlayer) {
      toast.error('Select a player first', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      return;
    }

    if (isStealMode && sq.value !== '') {
      if (sq.shielded) {
        toast.info('🛡️ Esta casilla está blindada para siempre. Elegí otra.', {
          position: "top-right", autoClose: 4000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
        });
        return; // no pierde el turno, solo avisa
      }
      if (stealCap !== 'unlimited' && (sq.claims || 0) >= stealCap) {
        toast.info(`Esta casilla ya llegó al tope de ${stealCap} jugadas y quedó bloqueada. Elegí otra.`, {
          position: "top-right", autoClose: 4000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
        });
        return; // no pierde el turno, solo avisa
      }
    } else if (!isStealMode && sq.value !== '') {
      return; // clásico: Square ya bloquea el click, resguardo
    }

    if (isStealMode && sq.value !== '' && (sq.claimedBy || []).includes(selectedPlayer._id)) {
      toast.warn('Ese jugador ya se usó en ESTA casilla. Elegí otro (podés usarlo en otra casilla).', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      setSelectedPlayer(null); // no pierde el turno, solo tiene que elegir otro jugador
      return;
    }

    attemptPlacement(square, selectedPlayer);
  }

  const reset = useCallback(() => {
    const newTurn = turn === 'X' ? 'O' : 'X';
    setSquares(Array(9).fill({ value: '', playerName: '', claims: 0, claimedBy: [], shielded: false }));
    setTurn(newTurn);
    setWinningSquares([]);
    setTimeRemaining(timeLimit);
    setConsecutiveSkips(0);
    setShieldUsed({ X: false, O: false });
    setArmedShieldPlayer(null);
    setLockedSquare(null);
    setRevealedItems(new Set());
    if (isFour12Mode) setFour12Character(prev => pickFour12Character(prev?.id ?? null));
    if (isClassicMode) nextClassicRival();
    handleChangeItems();
  }, [turn, timeLimit, handleChangeItems, isFour12Mode, isClassicMode, nextClassicRival]);

  const endGame = useCallback((result, winningPositions) => {
    setTurn(null);
    if (result != null) {
      setScore({
        ...score,
        [result]: score[result] + 1,
      })
    }

    setWinningSquares(winningPositions);
    setTimeout(reset, 3000);
    savePoints(gameToken, result, difficulty, league, 0, items);
  }, [score, reset, gameToken, difficulty, league, items]);

  const checkForWinner = useCallback((newSquares) => {
    for (let i = 0; i < winningPositions.length; i++) {
      const [a, b, c] = winningPositions[i];
      if (newSquares[a] && newSquares[a].value !== '' && newSquares[a].value === newSquares[b].value && newSquares[a].value === newSquares[c].value) {
        endGame(newSquares[a].value, winningPositions[i])
        return
      }
    }

    // Clásico: tablero lleno = empate. Robo: las casillas ocupadas siguen siendo robables, así
    // que el tablero lleno NO implica empate (la salvaguarda de partidas eternas es aparte,
    // ver el useEffect de consecutiveSkips más abajo).
    if (!isStealMode && !newSquares.some(square => square.value === '')) {
      endGame(null, Array.from(Array(10).keys()));
      return;
    }
    handleSkipTurn();
  }, [endGame, isStealMode]);

  // Robo y 412: si nadie pudo jugar en 2 vueltas completas (4 turnos salteados seguidos sin
  // ninguna jugada válida), se corta como empate en vez de trabarse para siempre. En 412 esto
  // cubre el caso de Skip Turn repetido sin llenar ninguna casilla.
  useEffect(() => {
    if ((isStealMode || isFour12Mode) && turn !== null && consecutiveSkips >= 4) {
      endGame(null, Array.from(Array(10).keys()));
    }
  }, [consecutiveSkips, isStealMode, isFour12Mode, turn, endGame]);

  // eslint-disable-next-line no-unused-vars
  const handleClickTitle = () => {
    navigate('/')
  }

  useEffect(() => {
    let timeOutId;
    const botLogic = () => {
      const fetchBotPlayers = async (item1, item2, league) => {
        try {
          const customLatinize = (text) => {
            return text.split(/(ñ)/gi).map(part => {
              // Si es "ñ", mantenlo igual, si no, aplica latinize
              return part.toLowerCase() === 'ñ' ? part : latinize(part);
            }).join('');
          };

          const response = await fetch(`${backendURL}/players/searchByCriteria?item1=${customLatinize(item1.toLowerCase())}&item2=${customLatinize(item2.toLowerCase())}&league=${league}`);
          const data = await response.json();
          return data;
        } catch (error) {
          console.error('Error fetching bot players:', error);
          return [];
        }
      };

      const order = pickBotSquareOrder(squares, difficulty, {
        isStealMode, stealCap, botSymbol: 'O', oppSymbol: 'X',
      });

      if (order.length === 0) {
        if (!isStealMode) {
          endGame(null, Array.from(Array(10).keys()));
        } else {
          setConsecutiveSkips(prev => prev + 1);
          handleSkipTurn();
        }
        return;
      }

      const delay = Math.random() * (10000 - 3000) + 3000; // Tiempo aleatorio entre 3 y 10 segundos
      timeOutId = setTimeout(async () => {
        const achievementKeys = [
          'champions',
          'balon_oro',
          'europa_league',
          'campeon_mundo',
          'eurocopa',
          'copa_america',
          'jjoo',
          'bota_oro',
          'goldenboy',
          'puskas',
          'nations_league',
          'premier_league',
          'triplete',
          'conference_league'
        ];

        // Recorre las casillas candidatas en orden de prioridad; en Modo Robo descarta
        // jugadores ya usados en ESA casilla (sí puede repetirlos en otra) y prueba la
        // siguiente candidata si no queda ninguno disponible.
        for (const chosenSquare of order) {
          const candidateSq = squares[chosenSquare];
          if (candidateSq.shielded) continue; // nunca tocar una casilla blindada
          const [item1, item2] = itemMaping[chosenSquare];
          const rawBotPlayers = await fetchBotPlayers(item1, item2, league);
          const botPlayers = isStealMode
            ? rawBotPlayers.filter(p => !(candidateSq.claimedBy || []).includes(p._id))
            : rawBotPlayers;

          if (botPlayers.length === 0) continue;

          // 1. Calcular para cada jugador cuántos logros tiene
          const playersWithCounts = botPlayers.map(player => {
            const count = achievementKeys.reduce(
              (sum, key) => sum + (player[key] ? 1 : 0),
              0
            );
            return { player, count };
          });

          // 2. Preferir jugadores con imagen; solo usar sin imagen si ninguno la tiene
          const withImg = playersWithCounts.filter(p => p.player.img);
          const pool = withImg.length > 0 ? withImg : playersWithCounts;

          // 3. Filtrar los que tengan más de 4 logros (del pool preferido)
          const aboveFour = pool
            .filter(p => p.count > 4)
            .map(p => p.player);

          let botPlayer;
          if (aboveFour.length > 0) {
            // 4a. Si hay jugadores con >4 logros, elijo uno al azar entre ellos
            botPlayer = aboveFour[Math.floor(Math.random() * aboveFour.length)];
          } else {
            // 4b. Si no, busco el máximo de logros y elijo al azar entre los que empatan
            const maxCount = Math.max(...pool.map(p => p.count));
            const topPlayers = pool
              .filter(p => p.count === maxCount)
              .map(p => p.player);
            botPlayer = topPlayers[Math.floor(Math.random() * topPlayers.length)];
          }

          // Modo 412: el bot "clickea" la casilla primero — se revela fila+columna y queda
          // resaltada, igual que cuando lo hace el humano — y recién tras una pausa breve pone
          // al jugador. Sin esto, la jugada del bot aparecía resuelta de golpe (revelado +
          // jugador al mismo tiempo), rompiendo el efecto de "primero elijo, después busco" que
          // sí tiene el humano.
          if (isFour12Mode) {
            setLockedSquare(chosenSquare);
            const [colIdx, rowIdx] = squareToItemIndices(chosenSquare);
            setRevealedItems(prev => new Set(prev).add(colIdx).add(rowIdx));
            await new Promise(resolve => setTimeout(resolve, 1300));
          }

          // 5. Pinto la jugada con el nombre del jugador elegido
          const sq = candidateSq;
          const wasSteal = isStealMode && sq.value !== '' && sq.value !== 'O';
          const wasReinforce = isStealMode && sq.value === 'O'; // reforzó su propia casilla
          const stolenFromName = sq.playerName;
          const newSquares = [...squares];
          newSquares.splice(chosenSquare, 1, {
            value: 'O',
            playerName: botPlayer.nombre,
            playerImg: botPlayer.img || null,
            claims: (sq.claims || 0) + 1,
            claimedBy: isStealMode ? [...(sq.claimedBy || []), botPlayer._id] : (sq.claimedBy || []),
            // Reforzar la propia casilla la blinda gratis al instante (no gasta el blindaje único).
            shielded: wasReinforce ? true : (sq.shielded || false),
          });
          const botShields = !wasReinforce && isStealMode && shouldBotShield(
            chosenSquare, newSquares, 'O', difficulty, shieldUsed.O, { oppSymbol: 'X', stealCap }
          );
          if (botShields) newSquares[chosenSquare] = { ...newSquares[chosenSquare], shielded: true };
          setConsecutiveSkips(0);
          checkForWinner(newSquares);
          setSquares(newSquares);
          if (isFour12Mode) setLockedSquare(null); // ya puso al jugador: libera el resaltado
          if (botShields) {
            setShieldUsed(prev => ({ ...prev, O: true }));
            if (wasSteal) {
              setStealFlashIdx(chosenSquare);
              setTimeout(() => setStealFlashIdx(null), 1200);
            }
            toast.info(
              wasSteal
                ? `🛡️🔥 ¡El bot le robó la casilla a ${stolenFromName} y la blindó para siempre!`
                : '🛡️ ¡El bot blindó una casilla para siempre!',
              {
                position: "top-right", autoClose: 4000, hideProgressBar: false,
                closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
              }
            );
          } else if (wasReinforce) {
            toast.info('🛡️ El bot reforzó su casilla: quedó blindada para siempre (no gastó su blindaje).', {
              position: "top-right", autoClose: 4000, hideProgressBar: false,
              closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
            });
          } else if (wasSteal) {
            setStealFlashIdx(chosenSquare);
            setTimeout(() => setStealFlashIdx(null), 1200);
            toast.info(`🔥 ¡El bot le robó la casilla a ${stolenFromName}!`, {
              position: "top-right", autoClose: 3000, hideProgressBar: false,
              closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
            });
          }
          return;
        }

        // Ninguna candidata tenía jugadores disponibles.
        if (isStealMode) setConsecutiveSkips(prev => prev + 1);
        handleSkipTurn();
      }, delay);
    }

    if (turn === 'O') {
      botLogic();
    }

    return () => clearTimeout(timeOutId);
  }, [turn, squares, endGame, itemMaping, checkForWinner, difficulty, league, isStealMode, stealCap, shieldUsed.O, isFour12Mode]);

  return (
    <div className="container">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <Navbar gamePage />

      <div className="game-page">
      <h1 className="game-title">Footy <span>TicTacToe</span></h1>
      <div className="game-layout">

        {/* ── LEFT panel: Bot character (desktop) ── */}
        <aside className="game-panel game-panel--left">
          <BotCharacter
            turn={turn}
            timeRemaining={timeRemaining}
            score={score}
            squares={squares}
            timeLimit={timeLimit}
            difficulty={difficulty}
            wrongPickTrigger={wrongPickCount}
            character={isFour12Mode ? four12Character : classicRival.character}
            footer={isClassicMode
              ? <RivalPicker choice={classicRival.choice} onChange={classicRival.setChoice} />
              : null}
          />
        </aside>

        {/* ── Center: board ── */}
        <main className="game-center">
          <Board
            winningSquares={winningSquares}
            turn={turn}
            squares={squares}
            onClick={handleClick}
            clubSquares={items}
            handlePlayerSelect={handlePlayerSelect}
            player={turn}
            playerFootball={selectedPlayer}
            mode='bot'
            league={league}
            isStealMode={isStealMode}
            stealCap={stealCap}
            stealFlashSquare={stealFlashIdx}
            isFour12Mode={isFour12Mode}
            lockedSquare={lockedSquare}
            revealedItems={revealedItems}
          />
          {selectedPlayer && <PlayerSelected player={selectedPlayer} />}
        </main>

        {/* ── RIGHT panel: score+timer on top / controls below ── */}
        <aside className="game-panel game-panel--right">
          {/* Top: score + timer */}
          <div className="right-panel-top">
            <ScoreBoard scoreO={score.O} scoreX={score.X} turn={turn} />
            {timeRemaining !== null && (
              <div className="panel-card panel-card--timer">
                <span className="sidebar-label">Time</span>
                <CountdownTimer timeRemaining={timeRemaining} />
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="right-panel-divider" />

          {/* Bottom: difficulty + buttons */}
          <div className="right-panel-controls">
            <select
              className="btn-game difficulty-selector"
              value={difficulty}
              onChange={e => changeDifficulty(e.target.value, setDifficulty, 0, () => {})}
              disabled={hasAnyData}
            >
              <option value="very-easy">Very Easy</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="very-hard">Very Hard</option>
            </select>

            {isStealMode && turn === 'X' && (
                <button
                    className={`btn-game btn-shield${armedShieldPlayer === 'X' ? ' btn-shield--armed' : ''}`}
                    disabled={shieldUsed.X}
                    onClick={() => setArmedShieldPlayer(prev => prev === 'X' ? null : 'X')}
                >
                    {shieldUsed.X
                        ? '🛡️ Blindaje ya usado'
                        : armedShieldPlayer === 'X'
                            ? '🛡️ Blindaje armado (cancelar)'
                            : '🛡️ Blindar próxima jugada'}
                </button>
            )}

            <NextButton onClick={reset} changeTurn={handleSkipTurn} turn={turn} mode='bot' setStreak={() => {}} />

            <button className="btn-game btn-block" onClick={() => setShowBlockModal(true)} disabled={hasAnyData}>
              🔒 Block Teams
            </button>
          </div>
        </aside>

        {/* ── Mobile info row (score + timer + difficulty) ── */}
        <div className="mobile-info-row">
          <div className="mobile-score-group">
            <ScoreBoard scoreO={score.O} scoreX={score.X} turn={turn} />
            {timeRemaining !== null && (
              <div className="panel-card panel-card--timer">
                <span className="sidebar-label">Time</span>
                <CountdownTimer timeRemaining={timeRemaining} />
              </div>
            )}
          </div>
          <div className="difficulty-area">
            <span className="difficulty-label">Difficulty</span>
            <select
              className="difficulty-selector"
              value={difficulty}
              onChange={e => changeDifficulty(e.target.value, setDifficulty, 0, () => {})}
              disabled={hasAnyData}
            >
              <option value="very-easy">Very Easy</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="very-hard">Very Hard</option>
            </select>
          </div>
        </div>

      </div>

      {/* Anuncios: debajo del área de juego y, en pantallas muy anchas, columnas a los
          costados. Todos separados del tablero (ver AdBanner). */}
      <AdBanner placement="game" />
      <AdBanner placement="rail-left" />
      <AdBanner placement="rail-right" />

      {showBlockModal && (
        <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🛡️ Block Teams</h2>
            <p>You can block up to <strong>3 teams</strong>.</p>
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              <img
                src={obvioBotImg}
                alt="Obvio Bot"
                style={{ maxWidth: '120px', width: '100%', height: 'auto', objectFit: 'contain' }}
              />
            </div>
            <div className="club-list">
              {Object.entries(clubMapping).map(([label, value]) => (
                <label key={value} className="club-option">
                  <input
                    type="checkbox"
                    checked={blockedTeams.includes(value)}
                    disabled={!blockedTeams.includes(value) && blockedTeams.length >= 3}
                    onChange={() => {
                      setBlockedTeams(prev =>
                        prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
                      );
                    }}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="modal-buttons">
              <button className="close-button" onClick={() => setShowBlockModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default SoloBotPage;